/**
 * E11 web-portal design spec §0 — the first portal UI that has to
 * *build* a CanonicalSchema from nothing rather than upload or edit
 * one (WebhookJob.tsx's own per-event payload/target schema). Reuses
 * SchemaReview.tsx's own flat, top-level-only field-list posture
 * (name / type / nullable, the same CANONICAL_TYPES select) rather
 * than inventing a new one, layering add/remove-row controls on top
 * of that exact table shape. Nested `children` stay out of scope for
 * v1 — the identical limitation SchemaReview.tsx's own docstring
 * already accepts, for the same reason: editing arbitrarily deep
 * structure in a flat table is out of proportion for v1.
 */

import type { CanonicalField } from '../api/client';
import { CANONICAL_TYPES } from '../pages/SchemaReview';

interface SchemaFieldListBuilderProps {
  label: string;
  fields: CanonicalField[];
  onChange: (fields: CanonicalField[]) => void;
}

export function SchemaFieldListBuilder({ label, fields, onChange }: SchemaFieldListBuilderProps) {
  function updateField(index: number, patch: Partial<CanonicalField>) {
    onChange(fields.map((field, i) => (i === index ? { ...field, ...patch } : field)));
  }

  function addField() {
    onChange([...fields, { name: '', type: 'string', nullable: false }]);
  }

  function removeField(index: number) {
    onChange(fields.filter((_, i) => i !== index));
  }

  return (
    <div className="schema-field-list-builder">
      <table className="schema-review-table">
        <thead>
          <tr>
            <th>{label}</th>
            <th>Type</th>
            <th>Nullable</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {fields.map((field, i) => (
            // Index as key: rows have no other stable identity until a name
            // is typed, and rows are only ever appended/removed, never
            // reordered.
            <tr key={i}>
              <td>
                <input
                  type="text"
                  placeholder="field name"
                  value={field.name}
                  onChange={(event) => updateField(i, { name: event.target.value })}
                  aria-label={`${label} field name ${i + 1}`}
                />
              </td>
              <td>
                <select
                  value={field.type}
                  onChange={(event) => updateField(i, { type: event.target.value })}
                  aria-label={`${label} field type ${i + 1}`}
                >
                  {CANONICAL_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={field.nullable}
                  onChange={(event) => updateField(i, { nullable: event.target.checked })}
                  aria-label={`${label} field ${i + 1} nullable`}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => removeField(i)}
                  disabled={fields.length <= 1}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="btn btn--ghost" onClick={addField}>
        Add field
      </button>
    </div>
  );
}
